import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateFor } from "@/lib/cache-registry";
import { ensurePoCustomerLink, type EnsurePoCustomerResult } from "@/lib/ensure-po-customer";

interface LinkCustomerPoInput {
  customerId: string;
  /** Prosjektet knappen ble trykket fra, for målrettet invalidering. */
  projectId?: string;
}

/**
 * VIB-2575: «Koble til PowerOffice» for en kunde uten PO-kobling.
 *
 * Kaller `ensure-po-customer`, som oppretter kunden i PO (EHF, med fallback
 * til e-postfaktura), lagrer koblingen og oppretter PO-prosjekt for kundens
 * åpne, eksterne prosjekter som mangler det. Aktør og feil logges server-side.
 *
 * Feilen vises med grunnen fra edge function-en (f.eks. at e-post eller
 * postnummer mangler), så PL vet hva som må rettes før neste forsøk.
 */
export function useLinkCustomerPo() {
  const qc = useQueryClient();
  return useMutation<EnsurePoCustomerResult, Error, LinkCustomerPoInput>({
    mutationFn: async ({ customerId }) => {
      const result = await ensurePoCustomerLink(customerId);
      if (result.method === "error") {
        throw new Error(result.error ?? "Ukjent feil ved kobling til PowerOffice");
      }
      return result;
    },
    onSuccess: () => {
      toast.success("Kunden er koblet til PowerOffice.");
    },
    onError: (err) => {
      toast.error("Kunne ikke koble kunden til PowerOffice", { description: err.message });
    },
    onSettled: async (_data, _err, { customerId, projectId }) => {
      // Koblingen skriver customers, og kan opprette PO-prosjekt på kundens
      // prosjekter. Både vellykket og feilet forsøk logges til event_log.
      await invalidateFor(qc, "customers", { customerId });
      await invalidateFor(qc, "projects", { projectId });
      await invalidateFor(qc, "event_log");
    },
  });
}
